import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from "../../Layout";
import {
    BookOpen,
    Clock,
    FileText,
    AlertCircle,
    GraduationCap,
    Loader,
    Plus,
    RefreshCw,
    Search,
    CheckCircle,
    X
} from 'lucide-react';
import { API_CONFIG } from '../../config/api';

const StudentAssessmentsNew = () => {
    const navigate = useNavigate();
    const API_BASE_URL = API_CONFIG.BASE_URL;

    // State management
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Get auth headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('accessToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    // Fetch assessments on mount
    useEffect(() => {
        fetchAssessments();
    }, []);

    // Fetch assigned assessments
    const fetchAssessments = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${API_BASE_URL}/student/assessments`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Fetched assessments:', data);
            setAssessments(data.assessments || []);
        } catch (err) {
            console.error("Error fetching assessments:", err);
            setError("Failed to load assessments. Please check if the backend is running.");
            setAssessments([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter assessments based on search term
    const filteredAssessments = assessments.filter(assessment =>
        assessment.setup?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assessment.setup?.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assessment.setup?.domain?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Navigate to marks entry (simplified - no separate details page)
    const goToMarksEntry = (assessmentId) => {
        navigate(`/assessments/${assessmentId}/marks-entry`);
    };

    // Show notification
    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusConfig = {
            'not_started': { text: 'Not Started', color: 'bg-gray-100 text-gray-800' },
            'in_progress': { text: 'In Progress', color: 'bg-blue-100 text-blue-800' },
            'submitted': { text: 'Submitted', color: 'bg-yellow-100 text-yellow-800' },
            'approved': { text: 'Approved', color: 'bg-green-100 text-green-800' },
            'revision_requested': { text: 'Needs Revision', color: 'bg-orange-100 text-orange-800' }
        };

        const config = statusConfig[status] || statusConfig['not_started'];

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.text}
            </span>
        );
    };

    // Get action button text
    const getActionButtonText = (status) => {
        if (status === 'not_started') return 'Start Assessment';
        if (status === 'submitted' || status === 'approved') return 'View Submission';
        if (status === 'revision_requested') return 'Revise';
        return 'Continue';
    };

    return (
        <Layout>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Notification */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                                    notification.type === 'error' ? 'bg-red-500 text-white' :
                                        'bg-blue-500 text-white'
                                }`}>
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                                <AlertCircle className="w-5 h-5" />}
                            <span className="font-[poppins]">{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                                style={{ backgroundColor: '#D67C2C' }}>
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                                My Assessments
                            </h1>
                            <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                View and complete your assigned assessments
                            </p>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                        <Search className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Search Assessments</h2>
                                </div>
                                <button
                                    onClick={fetchAssessments}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <RefreshCw className="w-5 h-5" />
                                    Refresh
                                </button>
                            </div>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search assessments by title, subject, or domain..."
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                />
                                <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="flex justify-center items-center p-12">
                            <Loader className="w-8 h-8 text-[#D67C2C] animate-spin" />
                            <span className="ml-3 text-lg text-gray-600">Loading assessments...</span>
                        </div>
                    ) : error ? (
                        <div className="flex justify-center items-center p-12 text-red-500">
                            <AlertCircle className="w-6 h-6 mr-2" />
                            <span>{error}</span>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
                            <div className="p-6 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                        <FileText className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                                        Assigned Assessments ({filteredAssessments.length})
                                    </h2>
                                </div>
                            </div>

                            <div className="p-6">
                                {filteredAssessments.length === 0 ? (
                                    <div className="flex flex-col justify-center items-center p-12 text-gray-500">
                                        <FileText className="w-16 h-16 mb-4 text-gray-300" />
                                        <p className="text-lg mb-3">No assessments found</p>
                                        <p className="text-sm text-gray-400">
                                            {searchTerm ? "Try adjusting your search terms." : "No assessments have been assigned to you yet."}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredAssessments.map((assessment) => (
                                            <div key={assessment.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                            {assessment.setup?.title || 'Untitled Assessment'}
                                                        </h3>
                                                        <div className="space-y-1 text-sm text-gray-600">
                                                            <div className="flex items-center gap-2">
                                                                <BookOpen className="w-4 h-4" />
                                                                <span>{assessment.setup?.subject || assessment.setup?.domain || 'N/A'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <GraduationCap className="w-4 h-4" />
                                                                <span>Grade {assessment.setup?.grade || 'N/A'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-4 h-4" />
                                                                <span>{assessment.setup?.duration ? `${assessment.setup.duration} min` : 'No time limit'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between mb-4">
                                                    <span className="text-sm font-medium text-gray-700">
                                                        Total: {assessment.max_marks || assessment.setup?.total_marks || 0} marks
                                                    </span>
                                                    {getStatusBadge(assessment.submission_status || 'not_started')}
                                                </div>

                                                <button
                                                    onClick={() => goToMarksEntry(assessment.id)}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    {getActionButtonText(assessment.submission_status || 'not_started')}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default StudentAssessmentsNew;
